import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { logger } from "../../observability/LoggerFactory";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import type { ActionState, StartPublicationParams } from "./useTaskDetailActions";
import type { paths } from "../../api/generated/api-types";
import { formatLocalDate } from "./FormatLocalDate";
import { PUBLICATION_FILTER_DEFAULTS } from "../shared/publicationFilterDefaults";
import {
  PublicationFilterFields,
  type PublicationFilterFieldsValue,
} from "../shared/PublicationFilterFields";

type PublicationMode = paths["/publications"]["post"]["requestBody"]["content"]["application/json"]["mode"];
type ListSiteRegionsResponse =
  paths["/sites/regions"]["get"]["responses"]["200"]["content"]["application/json"];

// SITE-08 питает только строку «Количество доступных сайтов»: пока запрос идёт или
// отказал, счётчик остаётся null, строка отсутствует, а форма остаётся рабочей
// (screen doc «Форма запуска публикации»). Сам отказ логируется на границе запроса.
type AvailableSiteCount = number | null;

export function PublicationLaunchSection(props: {
  taskType: "generation_anchor_topic" | "generation_relevant_topic" | "generation_relevant_topic_fixed" | "homepage_publication" | "sitewide_publication";
  onStartPublication: (params: StartPublicationParams) => void;
  state: ActionState;
}) {
  const [mode, setMode] = useState<PublicationMode>("immediate");
  const [scheduledAt, setScheduledAt] = useState("");
  const [deadlineDate, setDeadlineDate] = useState("");
  // Дефолты формы совпадают с PUB-01 «Параметры системного автозапуска»
  // (TaskDetail «Форма запуска публикации»).
  const [filter, setFilter] = useState<PublicationFilterFieldsValue>({
    dr: PUBLICATION_FILTER_DEFAULTS.dr,
    organicKeywords: PUBLICATION_FILTER_DEFAULTS.organicKeywords,
    age: PUBLICATION_FILTER_DEFAULTS.age,
    uniqueSites: PUBLICATION_FILTER_DEFAULTS.uniqueSites,
    includeSubdomains: PUBLICATION_FILTER_DEFAULTS.includeSubdomains,
  });
  const [availableSiteCount, setAvailableSiteCount] = useState<AvailableSiteCount>(null);

  function handleFilterChange<K extends keyof PublicationFilterFieldsValue>(
    key: K,
    value: PublicationFilterFieldsValue[K],
  ) {
    setFilter((previous) => ({ ...previous, [key]: value }));
  }
  const [targetSiteUrlsText, setTargetSiteUrlsText] = useState("");

  const parsedTargetSiteUrls = targetSiteUrlsText
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  const isOverrideMode = parsedTargetSiteUrls.length > 0;

  const nonHomepageCredentialType = props.taskType === "sitewide_publication" ? "sitewide" : "article";
  const credentialType =
    props.taskType === "homepage_publication" ? "homepage" : nonHomepageCredentialType;

  useEffect(() => {
    setAvailableSiteCount(null);
    fetchWithRefresh(`/api/sites/regions?credentialType=${credentialType}`)
      .then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as ListSiteRegionsResponse;
          const count = data.regions.reduce((sum, region) => sum + region.siteCount, 0);
          setAvailableSiteCount(count);
        } else {
          logger.error(
            { err: `HTTP ${response.status}`, ucId: "SITE-08", component: "PublicationLaunchSection", step: "loadAvailableSiteCount" },
            "Failed to load available site count",
          );
        }
      })
      .catch((error: unknown) => {
        logger.error(
          { err: error, ucId: "SITE-08", component: "PublicationLaunchSection", step: "loadAvailableSiteCount" },
          "Failed to load available site count",
        );
      });
  }, [credentialType]);

  const isLoading = props.state.kind === "loading";
  const isScheduledDateMissing = mode === "scheduled" && scheduledAt === "";
  const isDeadlineMissing = mode === "stretched" && deadlineDate === "";
  const showsGenericLaunchError = !isOverrideMode && mode !== "scheduled" && mode !== "stretched";
  const isSubmitDisabled = isLoading || isScheduledDateMissing || isDeadlineMissing;
  const todayDate = formatLocalDate(new Date());
  const launchError =
    props.state.kind === "error" ? (
      <Alert severity="error">{props.state.message}</Alert>
    ) : null;

  function handleSubmit() {
    if (isOverrideMode) {
      props.onStartPublication({
        mode,
        scheduledAt: mode === "scheduled" ? scheduledAt : undefined,
        deadlineDate: mode === "stretched" ? deadlineDate : undefined,
        filter: null,
        targetSiteUrls: parsedTargetSiteUrls,
      });
      return;
    }
    props.onStartPublication({
      mode,
      scheduledAt: mode === "scheduled" ? scheduledAt : undefined,
      deadlineDate: mode === "stretched" ? deadlineDate : undefined,
      filter: {
        ...filter,
        // Выбор региона донора на форме отсутствует: публикация подбирает сайты без
        // ограничения по региону (screen doc, скрытые поля фильтр-режима).
        regions: PUBLICATION_FILTER_DEFAULTS.regions,
        includeSecondary: PUBLICATION_FILTER_DEFAULTS.includeSecondary,
      },
      targetSiteUrls: null,
    });
  }

  return (
    <Box mb={3}>
      <Typography variant="h6" mb={2}>
        Запустить публикацию
      </Typography>

      {props.taskType === "sitewide_publication" && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: 400 }}>
          Sitewide-ссылки размещаются только через плагин Link Factory
        </Alert>
      )}

      <Box display="flex" flexDirection="column" gap={2} maxWidth={400}>
        <TextField
          label="URL целевых сайтов (по одному в строке)"
          multiline
          minRows={3}
          value={targetSiteUrlsText}
          onChange={(event) => setTargetSiteUrlsText(event.target.value)}
          disabled={isLoading}
          size="small"
          helperText="По одному URL в строке. Если поле заполнено — публикация выполнится только на сайтах из списка; фильтры игнорируются. Количество URL должно совпадать с количеством единиц контента (вариаций статей или предложений homepage)."
        />

        {isOverrideMode && launchError}

        <Box sx={{ opacity: isOverrideMode ? 0.5 : 1, pointerEvents: isOverrideMode ? "none" : "auto" }}>

        {availableSiteCount !== null && (
          <Typography variant="body2" color="text.secondary">
            Количество доступных сайтов: {availableSiteCount}
          </Typography>
        )}

        <Divider />

        <FormControl size="small" disabled={isLoading}>
          <InputLabel>Режим</InputLabel>
          <Select
            value={mode}
            label="Режим"
            onChange={(event) => setMode(event.target.value)}
          >
            <MenuItem value="immediate">Немедленная</MenuItem>
            <MenuItem value="scheduled">Отложенная</MenuItem>
            <MenuItem value="stretched">Растянутая до дедлайна</MenuItem>
          </Select>
        </FormControl>

        {mode === "scheduled" && (
          <Box display="flex" flexDirection="column" gap={1}>
            <TextField
              label="Дата и время публикации"
              type="datetime-local"
              value={scheduledAt}
              onChange={(event) => setScheduledAt(event.target.value)}
              disabled={isLoading}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
            />
            {!isOverrideMode && launchError}
          </Box>
        )}

        {mode === "stretched" && (
          <Box display="flex" flexDirection="column" gap={1}>
            <TextField
              label="Дедлайн"
              type="date"
              value={deadlineDate}
              onChange={(event) => setDeadlineDate(event.target.value)}
              disabled={isLoading}
              size="small"
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayDate } }}
              helperText="Система случайно распределит публикации по дням от сегодня до дедлайна. Расписание появится в блоке публикации после запуска"
            />
            {!isOverrideMode && launchError}
          </Box>
        )}

        <PublicationFilterFields
          value={filter}
          onChange={handleFilterChange}
          disabled={isLoading}
        />
        </Box>

        {showsGenericLaunchError && launchError}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {isLoading ? "Запуск…" : "Запустить публикацию"}
        </Button>
      </Box>
    </Box>
  );
}
